// A venue's page (spec/15 P-06): its cover, whether it is open now, where it is; the PCs it has with how many are free
// now and what an hour costs; the passes on sale with their real prices; the opening week, what is there and how to
// reach it. Over `GET /v1/public/venues/{slug}` and `/shop`; a venue not listed or not live is 404.
//
// Paying: a signed-in player with a wallet at this venue's café that takes it buys a pass the kiosk sells here with a
// Buy button, paid from that wallet and nothing else (`GET /v1/me/venues/{venueId}/passes`; ADR-0054 §6), and where the
// wallet holds too little the card says to top up at the counter. Everything else is bought at the counter (no online
// payment is live), said once, plainly, with no button that cannot work.

import type { MyVenuePassShop, PublicVenueShop, VenueAmenity } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Chip, HeroStat, PageHero, PageSkeleton, Skeleton } from '@clavian/ui-web';
import { Link, useParams } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { isNotFound } from '../api/problems';
import { useMyVenuePassShop, usePublicVenue, useSession, useVenueShop } from '../api/queries';
import { formatterFor, shortDuration } from '../lib/format';
import { addressLine, coverUrl, intervalsOf, isAllDay, WEEK_DAYS, type WeekDay } from '../lib/venue';
import { LoadFailure, NotFound, SectionHead } from '../shell/feedback';
import { NavIcon } from '../shell/nav-icons';
import { BuyPassAction, ShopWalletLine } from './BuyPass';
import { OnlineTopUp } from './OnlineTopUp';

export function VenuePage(): ReactNode {
  const { t } = useTranslation('player');
  const { slug } = useParams({ from: '/site/venues/$slug' });
  const venue = usePublicVenue(slug);
  const shop = useVenueShop(slug, venue.isSuccess);
  // Signed in: what this player can buy here from their wallet. A café they never dealt with is 404 (no wallet there).
  const signedIn = useSession().data != null;
  const mine = useMyVenuePassShop(venue.data?.id, signedIn && venue.isSuccess);

  if (venue.isPending) {
    return (
      <div className="pw-page">
        <PageSkeleton label={t('app.loading')} />
      </div>
    );
  }
  if (venue.isError) {
    return isNotFound(venue.error) ? <NotFound /> : <LoadFailure error={venue.error} onRetry={() => void venue.refetch()} />;
  }

  const data = venue.data;
  const cover = coverUrl(data);
  const where = addressLine(data.address);
  const amenities = data.amenities as readonly VenueAmenity[];
  const mapsQuery = encodeURIComponent([data.name, data.address?.line1, data.address?.locality].filter(Boolean).join(', '));

  return (
    <div className="pw-page pw-venue">
      <Link to="/venues" className="pw-back">
        <NavIcon name="back" size={18} />
        {t('venue.back')}
      </Link>

      <div className="pw-venue__cover">
        {cover ? (
          <img src={cover} alt="" decoding="async" width={1280} height={480} />
        ) : (
          <span className="pw-venue__cover-placeholder" aria-hidden="true">
            {data.name.slice(0, 1)}
          </span>
        )}
      </div>

      <PageHero
        className="pw-venue__hero"
        title={data.name}
        status={
          <span className={`pw-pill ${data.openNow ? 'pw-pill--open' : 'pw-pill--closed'}`}>
            {data.openNow ? t('venue.openNow') : t('venue.closed')}
          </span>
        }
        {...(where ? { meta: where } : {})}
        actions={
          <span className="pw-venue__links">
            <a
              className="pw-btn pw-btn--secondary"
              href={`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`}
              target="_blank"
              rel="noreferrer noopener"
            >
              <NavIcon name="venues" size={18} />
              {t('venue.directions')}
            </a>
            {data.contact.phone ? (
              <a className="pw-btn pw-btn--secondary" href={`tel:${data.contact.phone}`}>
                {t('venue.phone')}
              </a>
            ) : null}
          </span>
        }
        stats={heroStats(shop, t)}
      />

      {shop.isPending ? (
        <Skeleton height="10rem" />
      ) : shop.isError ? null : (
        <Shop shop={shop.data} mine={mine.data?.walletEnabled === true || mine.data?.onlinePayments === true ? mine.data : undefined} />
      )}

      <div className="pw-venue__grid">
        <section className="pw-panel" aria-labelledby="pw-venue-hours">
          <SectionHead id="pw-venue-hours" title={t('venue.hours')} />
          <dl className="pw-hours">
            {WEEK_DAYS.map((day) => (
              <HoursRow key={day} day={day} intervals={intervalsOf(data.openingHours, day)} />
            ))}
          </dl>
        </section>

        {amenities.length > 0 ? (
          <section className="pw-panel" aria-labelledby="pw-venue-amenities">
            <SectionHead id="pw-venue-amenities" title={t('venue.amenities')} />
            <ul className="pw-chip-row">
              {amenities.map((amenity) => (
                <li key={amenity}>
                  <Chip>{t(`venues.amenities.${amenity}`)}</Chip>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {data.contact.phone || data.contact.website || data.contact.email ? (
          <section className="pw-panel" aria-labelledby="pw-venue-contact">
            <SectionHead id="pw-venue-contact" title={t('venue.contact')} />
            <dl className="pw-facts">
              {data.contact.phone ? (
                <div className="pw-facts__row">
                  <dt>{t('venue.phone')}</dt>
                  <dd className="clav-num">
                    <a className="pw-link" href={`tel:${data.contact.phone}`}>
                      {data.contact.phone}
                    </a>
                  </dd>
                </div>
              ) : null}
              {data.contact.email ? (
                <div className="pw-facts__row">
                  <dt>{t('venue.email')}</dt>
                  <dd>
                    <a className="pw-link" href={`mailto:${data.contact.email}`}>
                      {data.contact.email}
                    </a>
                  </dd>
                </div>
              ) : null}
              {data.contact.website ? (
                <div className="pw-facts__row">
                  <dt>{t('venue.website')}</dt>
                  <dd>
                    <a className="pw-link" href={data.contact.website} target="_blank" rel="noreferrer noopener">
                      {data.contact.website.replace(/^https?:\/\//, '')}
                    </a>
                  </dd>
                </div>
              ) : null}
            </dl>
          </section>
        ) : null}
      </div>
    </div>
  );
}

/** The figures under the venue's name, from the shop it already loads: PCs free now, passes on sale, the lowest hourly rate. */
function heroStats(shop: ReturnType<typeof useVenueShop>, t: (key: string, options?: Record<string, unknown>) => string): ReactNode {
  if (shop.isPending) return [0, 1, 2].map((key) => <Skeleton key={key} height="4.25rem" />);
  if (shop.isError) return undefined;
  const classes = shop.data.stationClasses;
  const free = classes.reduce((sum, cls) => sum + cls.free, 0);
  const total = classes.reduce((sum, cls) => sum + cls.total, 0);
  const rates = classes.flatMap((cls) => (cls.hourlyRate === null ? [] : [cls.hourlyRate]));
  const lowest = rates.reduce<(typeof rates)[number] | undefined>((best, rate) => (best === undefined || rate.amount < best.amount ? rate : best), undefined);
  const stats: ReactNode[] = [];
  if (total > 0) stats.push(<HeroStat key="free" label={t('venue.stats.free')} value={free} of={t('venue.stats.of', { total })} />);
  if (shop.data.passes.length > 0) stats.push(<HeroStat key="passes" label={t('venue.stats.passes')} value={shop.data.passes.length} />);
  if (lowest !== undefined) stats.push(<HeroStat key="rate" label={t('venue.stats.rate')} value={formatterFor(lowest.currency).money(lowest)} />);
  return stats.length === 0 ? undefined : stats;
}

function Shop({ shop, mine }: { readonly shop: PublicVenueShop; readonly mine: MyVenuePassShop | undefined }): ReactNode {
  const { t } = useTranslation('player');
  // A pass the player can buy here from their wallet: the kiosk's passes, priced for them. Others are bought at the counter.
  const buyable = new Map((mine?.passes ?? []).map((pass) => [pass.id, pass]));
  return (
    <>
      <section className="pw-panel" aria-labelledby="pw-venue-pcs">
        <SectionHead id="pw-venue-pcs" title={t('venue.pcs.title')} />
        {shop.stationClasses.length === 0 ? (
          <p className="pw-muted">{t('venue.pcs.none')}</p>
        ) : (
          <>
            <ul className="pw-classes">
              {shop.stationClasses.map((cls) => (
                <li key={cls.name} className="pw-class" data-free={cls.free > 0 ? 'yes' : 'no'}>
                  <span className="pw-class__name">{cls.name}</span>
                  <span className="pw-class__free">
                    <span className="pw-class__dot" aria-hidden="true" />
                    {t('venue.pcs.free', { free: cls.free, total: cls.total })}
                  </span>
                  <span className="pw-class__rate clav-num">
                    {cls.hourlyRate === null ? t('venue.pcs.passesOnly') : t('venue.pcs.rate', { amount: formatterFor(cls.hourlyRate.currency).money(cls.hourlyRate) })}
                  </span>
                </li>
              ))}
            </ul>
            <p className="pw-subtle">{t('venue.pcs.note')}</p>
          </>
        )}
      </section>

      <section className="pw-panel" aria-labelledby="pw-venue-passes">
        <SectionHead id="pw-venue-passes" title={t('venue.passes.title')} />
        {mine === undefined || !mine.walletEnabled || (buyable.size === 0 && mine.topUp === null) ? null : <ShopWalletLine shop={mine} />}
        {mine === undefined || mine.topUp === null ? null : <OnlineTopUp shop={mine} options={mine.topUp} />}
        {shop.passes.length === 0 ? (
          <p className="pw-muted">{t('venue.passes.none')}</p>
        ) : (
          <ul className="pw-shop">
            {shop.passes.map((pass) => (
              <li key={pass.id} className="pw-shop__pass">
                <div className="pw-shop__top">
                  <h3 className="pw-shop__name">{pass.name}</h3>
                  <span className="pw-shop__price clav-num">{formatterFor(pass.price.currency).money(pass.price)}</span>
                </div>
                <p className="pw-shop__play">{t('venue.passes.play', { duration: shortDuration(pass.durationSeconds) })}</p>
                <ul className="pw-shop__facts">
                  {pass.window === '' ? null : <li>{t('venue.passes.window', { window: pass.window })}</li>}
                  {pass.validity === '' ? null : <li>{t('venue.passes.validity', { validity: pass.validity })}</li>}
                  {pass.appliesTo === '' ? null : <li>{t('venue.passes.for', { classes: pass.appliesTo })}</li>}
                </ul>
                {mine === undefined || !buyable.has(pass.id) ? null : (
                  <div className="pw-shop__buy">
                    <BuyPassAction shop={mine} pass={buyable.get(pass.id)!} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        {shop.onlinePayments ? null : (
          <div className="pw-paynote" role="note">
            <NavIcon name="wallet" size={20} />
            <div>
              <p className="pw-paynote__title">{t('venue.pay.title')}</p>
              <p className="pw-muted">{mine === undefined || buyable.size === 0 ? t('venue.pay.body') : t('venue.pay.walletBody')}</p>
            </div>
          </div>
        )}
      </section>
    </>
  );
}

function HoursRow({ day, intervals }: { readonly day: WeekDay; readonly intervals: ReturnType<typeof intervalsOf> }): ReactNode {
  const { t } = useTranslation('player');
  const text =
    intervals.length === 0
      ? t('venue.dayClosed')
      : intervals.map((interval) => (isAllDay(interval) ? t('venue.allDay') : `${interval.opens} to ${interval.closes}`)).join(', ');
  return (
    <div className="pw-hours__row">
      <dt>{t(`venue.days.${day}`)}</dt>
      <dd className="clav-num">{text}</dd>
    </div>
  );
}
